<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Chart from 'chart.js/auto'
import { area as turfArea } from '@turf/turf'
import { API, HOST } from '@/utils/http'
import { Button } from '@/components/ui/button'
import { LulcStackedBarChart } from '@/components/layers'

const route = useRoute()
const store = useStore()

const props = defineProps({
  layer: { type: Object, required: true },
})

const orgId = computed(() => route.params.org_id)

// For new LULC group rows the layer_lulc data lives on the classified child
// (type=7, data_type=raster). For old single rows it's on the layer itself.
const classifiedChild = computed(() =>
  (props.layer?.properties?.children || []).find(c => c.properties?.type === 7) || null
)
const lulc = computed(() => classifiedChild.value?.properties?.layer || props.layer?.properties?.layer || {})
// activeMonthKey is the vuex lulcActiveMonth key. LayerLulcClassified is passed parent-code=item.uuid
// where item is the classified child. For old single rows the layer IS the item, so use its uuid.
const activeMonthKey = computed(() => classifiedChild.value?.uuid || props.layer?.uuid)
// parentCode kept as the top-level layer uuid (used for UI only, e.g. route params)
const parentCode = computed(() => props.layer?.uuid)

const aoiGeometry = computed(() => lulc.value?.geometry || lulc.value?.aoi || null)

const aoiAreaHa = computed(() => {
  const geom = aoiGeometry.value
  if (!geom) return null
  try {
    const m2 = turfArea({ type: 'Feature', geometry: geom, properties: {} })
    return m2 / 10000
  } catch (e) {
    return null
  }
})

const months = computed(() => {
  const list = Array.isArray(lulc.value?.months) ? lulc.value.months : []
  return [...list].sort((a, b) =>
    String(a.classification_month).localeCompare(String(b.classification_month))
  )
})

const doneMonths = computed(() => months.value.filter(m => Number(m.status) === 0))

const monthRangeLabel = computed(() => {
  const list = months.value
  if (!list.length) return ''
  const first = formatMonthLabel(list[0].classification_month)
  if (list.length === 1) return first
  const last = formatMonthLabel(list[list.length - 1].classification_month)
  return `${first} – ${last}`
})

const STATUS_LABELS = {
  0: 'Done',
  1: 'Initializing',
  2: 'Waiting for model',
  3: 'Downloading basemap',
  4: 'Classifying',
  5: 'Rendering tiles',
  '-1': 'Failed — model',
  '-2': 'Failed — basemap',
  '-3': 'Failed — classification',
  '-4': 'Failed — tiles',
}

const aggregateStatusLabel = computed(() => {
  const s = lulc.value?.status
  if (s === undefined || s === null) return '—'
  return STATUS_LABELS[String(s)] ?? `status=${s}`
})

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatMonthLabel = (key) => {
  if (!key) return ''
  const parts = String(key).split('_')
  if (parts.length !== 2) return key
  const year = Number(parts[0])
  const month = Number(parts[1])
  if (!year || !month) return key
  return `${MONTH_LABELS[month - 1]} ${year}`
}

const activeMonth = ref(null)

const ensureActiveMonth = () => {
  if (!doneMonths.value.length) {
    activeMonth.value = null
    return
  }
  const stored = store.getters['layers/getLulcActiveMonth'](activeMonthKey.value)
  const fromStore = stored ? doneMonths.value.find(m => m.code === stored) : null
  activeMonth.value = fromStore || doneMonths.value[doneMonths.value.length - 1]
  if (activeMonth.value) {
    store.dispatch('layers/setLulcActiveMonth', {
      parentCode: activeMonthKey.value,
      monthCode: activeMonth.value.code,
    })
  }
}

const onChartHover = (month) => {
  if (!month || Number(month.status) !== 0) return
  if (activeMonth.value?.code === month.code) return
  activeMonth.value = month
  store.dispatch('layers/setLulcActiveMonth', {
    parentCode: activeMonthKey.value,
    monthCode: month.code,
  })
}

const statRows = computed(() => {
  const stats = activeMonth.value?.stats
  if (!stats || typeof stats !== 'object') return []
  return Object.entries(stats)
    .map(([name, v]) => ({
      name,
      class_id: v?.class_id,
      area_ha: Number(v?.area_ha ?? 0),
      percentage: Number(v?.percentage ?? 0),
      pixels: Number(v?.pixels ?? 0),
    }))
    .sort((a, b) => b.percentage - a.percentage)
})

// Model details (training month + labels) live on the lulc_model row; fetch by code.
const model = ref(null)
const modelLoading = ref(false)

const fetchModel = async () => {
  const code = lulc.value?.model_code
  if (!code || !orgId.value) {
    model.value = null
    return
  }
  modelLoading.value = true
  try {
    const res = await API.get(`${HOST}/org/${orgId.value}/lulc/models/${code}`)
    model.value = res.data || null
  } catch (e) {
    console.warn('Failed to fetch LULC model', e)
    model.value = null
  } finally {
    modelLoading.value = false
  }
}

// Authoritative class metadata: order + color come from labels_meta.json captured
// at training time and persisted in lulc_model.metadata.classes by tulum.
// Each entry: { class_code, name, color_hex, template_id, feature_count }.
const modelClasses = computed(() => {
  const arr = model.value?.metadata?.classes
  if (Array.isArray(arr) && arr.length) {
    return [...arr].sort((a, b) => Number(a.class_code) - Number(b.class_code))
  }
  // Fallback: older models without `classes` — derive names from class_mapping
  // and synthesise colors so the UI doesn't fall over (won't match the tiles
  // exactly until those models are retrained).
  const mapping = model.value?.metadata?.class_mapping
  if (!mapping) return []
  return Object.keys(mapping)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => ({ class_code: Number(k), name: mapping[k], color_hex: null }))
})

// Synthetic palette for the legacy fallback path only (no class_colors persisted).
const FALLBACK_PALETTE = [
  '#2e7d32', '#9ccc65', '#fbc02d', '#ef6c00', '#5d4037',
  '#0288d1', '#7e57c2', '#c2185b', '#455a64', '#f06292',
]

const modelLabels = computed(() => modelClasses.value.map(c => c.name))

const classColors = computed(() => {
  const map = {}
  modelClasses.value.forEach((c, idx) => {
    map[c.name] = c.color_hex || FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length]
  })
  return map
})

const donutCanvas = ref(null)
let donutChart = null

const buildDonut = () => {
  if (!donutCanvas.value) return
  if (donutChart) {
    donutChart.destroy()
    donutChart = null
  }
  const rows = statRows.value.filter(r => r.area_ha > 0)
  if (!rows.length) return
  donutChart = new Chart(donutCanvas.value.getContext('2d'), {
    type: 'doughnut',
    data: {
      labels: rows.map(r => r.name),
      datasets: [{
        data: rows.map(r => r.area_ha),
        backgroundColor: rows.map(r => classColors.value[r.name] || '#9ca3af'),
        borderWidth: 1,
        borderColor: '#fff',
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const row = rows[ctx.dataIndex]
              if (!row) return ctx.formattedValue
              return `${row.name}: ${row.area_ha.toLocaleString(undefined, { maximumFractionDigits: 1 })} ha (${row.percentage.toFixed(1)}%)`
            },
          },
        },
      },
    },
  })
}

watch(statRows, buildDonut, { deep: true, flush: 'post' })
watch(classColors, buildDonut, { deep: true, flush: 'post' })
watch(doneMonths, ensureActiveMonth, { deep: true })

onMounted(() => {
  fetchModel()
  ensureActiveMonth()
  buildDonut()
})

onUnmounted(() => {
  if (donutChart) {
    donutChart.destroy()
    donutChart = null
  }
})

watch(() => lulc.value?.model_code, fetchModel)

const downloadAoi = () => {
  const geometry = aoiGeometry.value
  if (!geometry) return
  const geoJSON = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  }
  const fileName = (props.layer?.properties?.name || 'layer-aoi').replace(/[^\w.-]+/g, '_')
  const blob = new Blob([JSON.stringify(geoJSON, null, 2)], { type: 'application/json' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${fileName}.geojson`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}
</script>

<template>
  <div class="space-y-4 text-sm">
    <ul class="space-y-1">
      <li v-if="model && model.training_month">
        <span class="text-muted-foreground">Training month: </span>
        <span>{{ formatMonthLabel(model.training_month) }}</span>
      </li>
      <li v-if="modelLabels.length">
        <span class="text-muted-foreground">Labels: </span>
        <span>{{ modelLabels.join(', ') }}</span>
      </li>
      <li>
        <span class="text-muted-foreground">Status: </span>
        <span>{{ aggregateStatusLabel }}</span>
      </li>
      <li v-if="months.length">
        <span class="text-muted-foreground">Months: </span>
        <span>{{ monthRangeLabel }}</span>
      </li>
      <li v-if="aoiAreaHa !== null">
        <span class="text-muted-foreground">Area: </span>
        <span class="tabular-nums">{{ aoiAreaHa.toLocaleString(undefined, { maximumFractionDigits: 0 }) }} ha</span>
      </li>
    </ul>

    <LulcStackedBarChart
      v-if="months.length"
      :months="months"
      :class-names="modelLabels"
      :class-colors="classColors"
      :aoi-area-ha="aoiAreaHa"
      :active-month-code="activeMonth?.code"
      @update:activeMonth="onChartHover"
    />

    <div v-else class="text-xs text-muted-foreground">
      No classification months on this layer yet.
    </div>

    <div v-if="activeMonth" class="space-y-2">
      <div class="flex items-center justify-between">
        <span>{{ formatMonthLabel(activeMonth.classification_month) }}</span>
        <span class="text-xs text-muted-foreground">{{ STATUS_LABELS[String(activeMonth.status)] ?? `status=${activeMonth.status}` }}</span>
      </div>
      <div class="flex items-center gap-4">
        <div class="relative shrink-0" style="width: 150px; height: 150px">
          <canvas ref="donutCanvas"></canvas>
        </div>
        <ul v-if="statRows.length" class="flex-1 space-y-1 text-xs">
          <li
            v-for="row in statRows"
            :key="row.name"
            class="flex items-center justify-between gap-3"
          >
            <span class="flex items-center gap-2 min-w-0">
              <span
                class="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                :style="{ backgroundColor: classColors[row.name] || '#9ca3af' }"
              ></span>
              <span class="truncate">{{ row.name }}</span>
            </span>
            <span class="tabular-nums text-muted-foreground shrink-0">
              {{ row.area_ha.toLocaleString(undefined, { maximumFractionDigits: 1 }) }} ha · {{ row.percentage.toFixed(1) }}%
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="aoiGeometry" class="pt-2">
      <Button size="sm" variant="secondary" @click="downloadAoi">Download AOI</Button>
    </div>
  </div>
</template>
